import { weatherIconName } from '../../utils/weatherIcon';

/**
 * A forecast condition as a small coloured glyph: a gold sun, a pale cloud
 * with a slate edge, blue rain and snow, a gold bolt (Kurtis 2026-11-01: the
 * grey outline icons read flat in the day headers). Colour is on the glyph
 * only — the temperature beside it stays plain text — and weather is the one
 * planner glyph allowed its own colours (style guide → planner colour
 * budget). The condition → glyph choice is weatherIconName's, so a day shows
 * the same weather in the itinerary header, the Timeline bar and the sheet.
 *
 * Decorative: callers give the reading (temperature, condition) in text.
 */

// Cloud outline in a 24-unit box, spanning x 3–21, y 6.4–18.5.
const CLOUD = 'M7 18.5h10a4 4 0 0 0 .6-7.96A5.5 5.5 0 0 0 6.9 10.6 4 4 0 0 0 7 18.5z';
const CLOUD_FILL = 'fill-surface-tertiary stroke-content-muted';
const SUN_FILL = 'fill-accent stroke-accent-dark';

const rays = (cx, cy, inner, outer) => [0, 45, 90, 135, 180, 225, 270, 315].map((deg) => {
  const r = (deg * Math.PI) / 180;
  const p = (len) => [cx + Math.cos(r) * len, cy + Math.sin(r) * len].map(n => n.toFixed(2));
  const [x1, y1] = p(inner);
  const [x2, y2] = p(outer);
  return <line key={deg} x1={x1} y1={y1} x2={x2} y2={y2} />;
});

// A cloud raised to leave room for what falls from it.
const highCloud = <path d={CLOUD} transform="translate(0 -3)" className={CLOUD_FILL} strokeWidth="1.25" />;

const drops = (xs) => (
  <g className="stroke-info" strokeWidth="2" strokeLinecap="round">
    {xs.map(x => <line key={x} x1={x} y1="17.5" x2={x - 1} y2="20.5" />)}
  </g>
);

const GLYPHS = {
  sun: (
    <>
      <g className="stroke-accent-dark" strokeWidth="1.75" strokeLinecap="round">{rays(12, 12, 7, 9.5)}</g>
      <circle cx="12" cy="12" r="4.5" className={SUN_FILL} strokeWidth="1" />
    </>
  ),
  sunCloud: (
    <>
      <g className="stroke-accent-dark" strokeWidth="1.5" strokeLinecap="round">{rays(9, 8.5, 5, 6.75)}</g>
      <circle cx="9" cy="8.5" r="3.4" className={SUN_FILL} strokeWidth="1" />
      <path d={CLOUD} transform="translate(3.5 3) scale(0.85)" className={CLOUD_FILL} strokeWidth="1.45" />
    </>
  ),
  cloud: <path d={CLOUD} className={CLOUD_FILL} strokeWidth="1.25" />,
  rain: <>{highCloud}{drops([10, 15])}</>,
  cloudRain: <>{highCloud}{drops([8, 12.5, 17])}</>,
  snowflake: (
    <>
      {highCloud}
      <g className="fill-info">
        <circle cx="8" cy="18.25" r="1.25" />
        <circle cx="12.5" cy="20.5" r="1.25" />
        <circle cx="17" cy="18.25" r="1.25" />
      </g>
    </>
  ),
  bolt: (
    <>
      {highCloud}
      <path d="M13 14.5l-3.25 4.25h2.5L11 22.5l4-5.25h-2.5l1.5-2.75z" className={SUN_FILL} strokeWidth="0.75" strokeLinejoin="round" />
    </>
  ),
  fog: (
    <>
      {highCloud}
      <g className="stroke-content-muted" strokeWidth="1.75" strokeLinecap="round">
        <line x1="5" y1="18" x2="19" y2="18" />
        <line x1="8" y1="21" x2="16" y2="21" />
      </g>
    </>
  ),
};

export default function WeatherGlyph({ condition, className = 'w-4 h-4' }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false" className={`flex-shrink-0 ${className}`}>
      {GLYPHS[weatherIconName(condition)] || GLYPHS.sunCloud}
    </svg>
  );
}

/** The rain-chance mark: a blue drop, distinct from any condition glyph. */
export function RainDrop({ className = 'w-3 h-3' }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" className={`flex-shrink-0 ${className}`}>
      <path d="M12 3c-.4.6-6.5 7.6-6.5 11.5a6.5 6.5 0 0 0 13 0C18.5 10.6 12.4 3.6 12 3z" className="fill-info" />
    </svg>
  );
}
